import { createFileRoute, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { LeaderboardView } from "@/components/audit/LeaderboardView";
import { getLeaderboard } from "@/lib/audit.functions";
import { SITE_URL } from "@/lib/i18n";
import { buildHead } from "@/lib/seo";

const leaderboardQueryOptions = (locale: "en" | "es") =>
  queryOptions({
    queryKey: ["leaderboard", locale],
    queryFn: () => getLeaderboard({ data: { locale } }),
  });

export const Route = createFileRoute("/audit/leaderboard")({
  loader: ({ context }) => context.queryClient.ensureQueryData(leaderboardQueryOptions("en")),
  head: ({ loaderData }) => {
    const title = "Top AI-Readable Shopify Stores: Shugert Leaderboard";
    const description =
      "The Shopify stores that score highest on our free AI Readability (AEO) audit, how well AI answer engines can find, quote and cite them.";
    const path = "/audit/leaderboard";
    const entries = loaderData?.entries ?? [];
    const itemList = entries.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: title,
          numberOfItems: entries.length,
          itemListElement: entries.map((e) => ({
            "@type": "ListItem",
            position: e.rank,
            url: `${SITE_URL}/audit/results/${e.slug}`,
            name: e.hostname,
          })),
        }
      : undefined;
    return buildHead({
      locale: "en",
      title,
      description,
      path,
      altPath: "/es/auditoria/tabla",
      jsonLd: itemList,
    });
  },
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold">Something went wrong</h1>
          <p className="mt-3 text-muted-foreground">We couldn't load the leaderboard right now.</p>
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="mt-6 inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground/40"
          >
            Try again
          </button>
        </div>
      </SiteShell>
    );
  },
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold">Leaderboard unavailable</h1>
      </div>
    </SiteShell>
  ),
  component: function AuditLeaderboardPage() {
    const { data } = useSuspenseQuery(leaderboardQueryOptions("en"));
    return (
      <SiteShell>
        <LeaderboardView data={data} locale="en" />
      </SiteShell>
    );
  },
});
